import {
  Box,
  Flex,
  HStack,
  Text,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { getFromUrl, serverRequest } from '../../api/Api';
import {
  IconButton,
  PrimaryButton,
  TextButton
} from '../../components/Buttons/Buttons';
import CounsellorsTable from '../../components/Cases/CounsellorsTable';
import DetailsView from '../../components/Counsellors/DetailsView';
import IconBox from '../../components/IconBox';
import { Icon } from '../../components/Icons';
import { SearchInput, TextField } from '../../components/Inputs/TextField';
import ModalView from '../../components/Modal';
import { Headline } from '../../components/Texts/Texts';
import DashboardLayout from '../../layouts/dashboard/DashboardLayout';
import { colors } from '../../theme/colors';
import { hexToRgbA } from '../../utils/helper';
import {
  generateCounsellorWelcomeEmail,
  generateAdminNotification
} from '../../utils/emailNotifications';
import {
  getCurrentRole,
  hasPermission,
  Permission
} from '../../constants/roles';

type StaffType = 'COUNSELLOR' | 'EMERGENCY_RESPONDER';

interface StaffListProps {
  staffType?: StaffType;
}

const STAFF_COPY: Record<
  StaffType,
  {
    pageTitle: string;
    addLabel: string;
    inviteHeadline: string;
    invitePlaceholder: string;
    deleteHeadline: string;
    existsError: string;
    deleteSuccess: string;
    mustBeError: string;
  }
> = {
  COUNSELLOR: {
    pageTitle: 'Counsellors',
    addLabel: 'Add Counsellor',
    inviteHeadline: 'An invite will be sent to the Counsellor’s Email',
    invitePlaceholder: 'Type counsellor’s email',
    deleteHeadline: 'You are about to permanently remove this counsellor',
    existsError: 'Counsellor already exists',
    deleteSuccess: 'Counsellor deleted successfully',
    mustBeError: 'User must be a counsellor'
  },
  EMERGENCY_RESPONDER: {
    pageTitle: 'Emergency Responders',
    addLabel: 'Add Emergency Responder',
    inviteHeadline: 'An invite will be sent to the Emergency Responder’s Email',
    invitePlaceholder: 'Type emergency responder’s email',
    deleteHeadline:
      'You are about to permanently remove this emergency responder',
    existsError: 'Emergency responder already exists',
    deleteSuccess: 'Emergency responder deleted successfully',
    mustBeError: 'User must be an emergency responder'
  }
};

const StaffList = ({ staffType = 'COUNSELLOR' }: StaffListProps) => {
  const copy = STAFF_COPY[staffType];
  const role = getCurrentRole();
  const canAddCounsellor = hasPermission(role, Permission.ADD_COUNSELLOR);
  const canDeleteCounsellor = hasPermission(role, Permission.DELETE_COUNSELLOR);
  const canInvite = canAddCounsellor;
  const canDelete = canDeleteCounsellor;
  const [searchKeyword, setSearchKeyword] = useState('');
  const {
    isOpen: showDetails,
    onOpen: openDetails,
    onClose: closeDetails
  } = useDisclosure();

  const {
    isOpen: showInvite,
    onOpen: openInvite,
    onClose: closeInvite
  } = useDisclosure();

  const {
    isOpen: showPrompt,
    onOpen: openPrompt,
    onClose: closePrompt
  } = useDisclosure();

  const toast = useToast();

  const [staff, setStaff] = useState([]);
  const [staffDetails, setStaffDetails] = useState(null);
  const [deleteId, setDeleteId] = useState('');
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [staffCount, setStaffCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchResults, setSearchResults] = useState([]);
  const PAGE_SIZE = 10;
  const [timeoutId, setTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const viewStaff = (member: any) => {
    setStaffDetails(member);
    openDetails();
  };

  const getStaff = async () => {
    try {
      const data = await getFromUrl(
        `users/counsellors?type=${staffType}&size=${PAGE_SIZE}&page=${currentPage}`
      );
      if (data !== undefined) {
        setStaff(data);
      }
    } catch (e: any) {
      return;
    }
  };

  const getStaffCount = async () => {
    try {
      const { count } = await getFromUrl('users/overview');
      if (staffType === 'EMERGENCY_RESPONDER') {
        setStaffCount(
          count?.emergencyResponders ||
            count?.emergency_responders ||
            count?.emergencyRespondersCount ||
            1
        );
        return;
      }
      setStaffCount(count?.counsellors);
    } catch (e: any) {
      return;
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setSearchKeyword('');
    setSearchResults([]);
    getStaffCount().catch((err) => console.error(err));
  }, [staffType]);

  useEffect(() => {
    getStaff().catch((err) => console.error(err));
  }, [currentPage, staffType]);

  const deleteStaff = async () => {
    setLoadingDelete(true);
    try {
      serverRequest({
        endpoint: `users/${deleteId}`,
        body: null,
        method: 'DELETE'
      }).then((res) => {
        if (res.status === 400) {
          toast({
            position: 'top',
            title: 'Error',
            description: copy.mustBeError,
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        } else {
          toast({
            position: 'top',
            title: 'Success',
            description: copy.deleteSuccess,
            status: 'success',
            duration: 3000,
            isClosable: true
          });
          getStaff().catch(() => {
            return;
          });
        }
      });
      setLoadingDelete(false);
      closeDeletePrompt();
    } catch (e: any) {
      setLoadingDelete(false);
    }
  };

  const openDeletePrompt = (member: any) => {
    openPrompt();
    setDeleteId(member._id);
  };

  const closeDeletePrompt = () => {
    closePrompt();
    setDeleteId('');
  };

  const inviteStaff = async () => {
    if (inviteEmail) {
      setLoadingInvite(true);
      const body = {
        email: inviteEmail,
        role: staffType
      };
      try {
        const response = await serverRequest({
          endpoint: 'users/create',
          body,
          method: 'POST'
        });
        if (response.status === 400) {
          toast({
            position: 'top',
            title: 'Error',
            description: copy.existsError,
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        } else {
          if (staffType === 'COUNSELLOR') {
            await sendCounsellorWelcomeEmail(inviteEmail);
            await sendAdminNotification(inviteEmail);
          }
          getStaff().catch((err) => console.error(err));
          toast({
            position: 'top',
            title: 'Success',
            description: `You've sent an invite to ${inviteEmail}`,
            status: 'success',
            duration: 3000,
            isClosable: true
          });
        }
        setLoadingInvite(false);
        setInviteEmail('');
        closeInvite();
        return response;
      } catch (e: any) {
        console.error(e);
        setLoadingInvite(false);
      }
    }
  };

  const sendCounsellorWelcomeEmail = async (email: string) => {
    try {
      const emailBody = generateCounsellorWelcomeEmail(email);

      await serverRequest({
        endpoint: 'notifications/email',
        body: emailBody,
        method: 'POST'
      });
    } catch (error) {
      console.error('Failed to send welcome email:', error);
    }
  };

  const sendAdminNotification = async (counsellorEmail: string) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

      const notificationBody = generateAdminNotification(
        counsellorEmail,
        currentUser.name || 'Admin'
      );

      await serverRequest({
        endpoint: 'notifications',
        body: notificationBody,
        method: 'POST'
      });
    } catch (error) {
      console.error('Failed to send admin notification:', error);
    }
  };

  const searchStaffList = async () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const searchTimeoutId = setTimeout(async () => {
      try {
        const endpoint = `users/counsellors/search?search=${searchKeyword}&type=${staffType}`;
        const results = await getFromUrl(endpoint);
        setSearchResults(results);
      } catch (e: any) {
        console.error(e.message);
      }
    }, 1000);
    setTimeoutId(searchTimeoutId);
  };

  useEffect(() => {
    if (searchKeyword.length) {
      searchStaffList().catch((err) => console.error(err));
    }
  }, [searchKeyword, staffType]);

  return (
    <DashboardLayout pageTitle={copy.pageTitle}>
      <Box pr='60px' pb='54px'>
        <Flex justifyContent='space-between' alignItems='center' mb='30px'>
          {canInvite ? (
            <IconButton
              label={copy.addLabel}
              icon={<Icon name='Plus' color={colors.white} filled />}
              onClick={openInvite}
              width={staffType === 'EMERGENCY_RESPONDER' ? 'fit-content' : undefined}
              px={staffType === 'EMERGENCY_RESPONDER' ? '24px' : undefined}
              minW={staffType === 'EMERGENCY_RESPONDER' ? '244px' : undefined}
            />
          ) : (
            <Box />
          )}
          <SearchInput
            w='244px'
            value={searchKeyword}
            onChange={({ target: { value } }) => setSearchKeyword(value)}
          />
        </Flex>

        {staffCount && staff.length ? (
          <CounsellorsTable
            onPageChange={onPageChange}
            currentPage={currentPage}
            counsellors={searchKeyword.length ? searchResults : staff}
            searchText={searchKeyword}
            counsellorsCount={staffCount}
            actionItem={({ counsellorData }: { counsellorData: any }) => (
              <HStack spacing='20px'>
                <IconBox
                  icon='Show'
                  iconColor={colors.dark_green}
                  bg={hexToRgbA(colors.dark_green, '0.15')}
                  iconSize={16}
                  boxSize='36px'
                  round
                  cursor='pointer'
                  onClick={() => viewStaff(counsellorData)}
                />

                {canDelete && (
                  <IconBox
                    icon='Close-Square'
                    iconColor={colors.error}
                    bg={hexToRgbA(colors.error, '0.15')}
                    iconSize={16}
                    boxSize='36px'
                    round
                    cursor='pointer'
                    onClick={() => openDeletePrompt(counsellorData)}
                  />
                )}
              </HStack>
            )}
          />
        ) : null}
        {
          <Text mx='auto' w='max-content' color={colors.deep_blue} mt='80px'>
            {searchKeyword.length && !searchResults.length
              ? 'No data found'
              : null}
          </Text>
        }
        <ModalView
          isOpen={showDetails}
          onClose={closeDetails}
          bg='gray_50'
          maxW='600px'
        >
          <DetailsView details={staffDetails} />
        </ModalView>
        <ModalView
          isOpen={showInvite}
          onClose={closeInvite}
          bg='gray_50'
          maxW='600px'
          noCloseBtn
        >
          <Box bg='gray_50' px='86px' py='53px' borderRadius='8px'>
            <Headline size='19px' color='gray' mb='28px'>
              {copy.inviteHeadline}
            </Headline>
            <TextField
              label={copy.invitePlaceholder}
              value={inviteEmail}
              onChange={setInviteEmail}
              bg='white'
            />
            <PrimaryButton
              label='Send Invite'
              onClick={inviteStaff}
              isLoading={loadingInvite}
            />
            <TextButton label='cancel' mt='24px' onClick={closeInvite} />
          </Box>
        </ModalView>
        <ModalView
          isOpen={showPrompt}
          onClose={closeDeletePrompt}
          bg='gray_50'
          maxW='454px'
          noCloseBtn
        >
          <Box bg='gray_50' px='55px' py='75px' borderRadius='8px'>
            <Headline size='19px' color='gray' mb='28px' textAlign='center'>
              {copy.deleteHeadline}
            </Headline>
            <PrimaryButton
              label='Proceed'
              background='error'
              onClick={deleteStaff}
              isLoading={loadingDelete}
            />
            <TextButton label='cancel' mt='24px' onClick={closeDeletePrompt} />
          </Box>
        </ModalView>
      </Box>
    </DashboardLayout>
  );
};

const Counsellors = () => {
  return <StaffList staffType='COUNSELLOR' />;
};

export const EmergencyResponders = () => {
  return <StaffList staffType='EMERGENCY_RESPONDER' />;
};

export default Counsellors;
